import { roundToPrecision } from '@/helpers/finance';
import { FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES } from '@/modules/finance/common/constants/rounding.constants';

import { GenerateBillSubRowType } from '../types';

/**
 * Sub-row types whose amounts the salary adjustment reconciles against.
 *
 * Overheads are deliberately excluded: they are bonuses / deductions that sit *on top of*
 * the agreed salary, so they must not be cancelled out by the adjustment. Everything else
 * (projects, added projects, time-offs) represents work already covered by the salary.
 */
const RECONCILED_SUB_ROW_TYPES: ReadonlySet<GenerateBillSubRowType> = new Set([
  GenerateBillSubRowType.PROJECT,
  GenerateBillSubRowType.ADDED_PROJECT,
  GenerateBillSubRowType.TIME_OFF,
]);

export const isReconciledAgainstSalary = (type: GenerateBillSubRowType): boolean =>
  RECONCILED_SUB_ROW_TYPES.has(type);

/**
 * The amount a single hours × rate line actually bills.
 *
 * Rounded to the currency's precision because that is the figure that reaches QuickBooks — the
 * request mapper rounds every line independently, so this is the real per-line contribution.
 *
 * Reconciliation MUST go through this rather than summing raw `hours * rate` products. With a
 * repeating rate (e.g. 4000 / 176 = 22.72727273) each line's rounding error is a fraction of a
 * cent, but they accumulate: three lines once summed to 3727.2727 raw vs 3727.28 billed, so an
 * adjustment computed from the raw sum was a cent too small and the bill totalled 4000.01 instead
 * of the agreed 4000.00 (OPS-1541).
 */
export const calculateLineAmount = ({ hours, rate }: { hours: number; rate: number }): number =>
  roundToPrecision(hours * rate, FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES);

/**
 * Total of the lines the salary absorbs, summed from each line's *billed* (rounded) amount.
 *
 * Summing the rounded parts is what makes `Σ(lines) + adjustment === salary` hold exactly.
 */
export const sumReconciledLineAmounts = (
  lines: readonly { hours: number; rate: number }[]
): number =>
  roundToPrecision(
    lines.reduce((sum, line) => sum + calculateLineAmount(line), 0),
    FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES
  );

/**
 * The adjustment that brings the selected billable lines up (or down) to the agreed salary.
 *
 * Deliberately allowed to be negative: when the selected project lines already exceed the
 * salary, a negative adjustment is what keeps the member's bill total equal to the salary.
 *
 * @param monthlySalaryAmount - the agreed monthly salary for the billing period
 * @param reconciledLinesTotal - summed amount of selected project / added-project / time-off lines
 */
export const calculateSalaryAdjustment = ({
  monthlySalaryAmount,
  reconciledLinesTotal,
}: {
  monthlySalaryAmount: number;
  reconciledLinesTotal: number;
}): number =>
  roundToPrecision(
    monthlySalaryAmount - reconciledLinesTotal,
    FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES
  );

/**
 * True when the adjustment reconciles to exactly nothing — the selected lines already sum to
 * the agreed salary, so there is no top-up or claw-back to bill.
 *
 * Such a line is shown greyed out (so the user can still see the reconciliation balanced) but
 * is omitted from the payload: a 0.00 item would be noise on the bill and in QuickBooks.
 *
 * Compared post-rounding so a sub-cent floating-point residue still counts as zero.
 */
export const isSalaryAdjustmentEmpty = (adjustmentAmount: number): boolean =>
  roundToPrecision(adjustmentAmount, FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES) === 0;

/** Stable sub-row id for a member's salary-adjustment line. */
export const getSalaryAdjustmentId = (memberId: string | number): string =>
  `salary-adjustment-${memberId}`;

/**
 * Quantity billed for a salary-only line. The salary is a flat monthly amount, so it bills
 * as a single unit priced at the full salary rather than as hours.
 */
export const SALARY_LINE_QUANTITY = 1;

/**
 * True when the member's bill will consist of nothing but their monthly salary — i.e. salary
 * mode is on and no project / time-off / overhead line is selected.
 *
 * In that case the bill is sent as a single SALARY line (department product/service, member
 * name, quantity 1, rate = salary, no project attribution) rather than as a reconciliation
 * adjustment sitting alongside other lines.
 */
export const isSalaryOnlyBill = ({
  isMonthlySalaryEnabled,
  selectedBillableLinesCount,
}: {
  isMonthlySalaryEnabled: boolean;
  selectedBillableLinesCount: number;
}): boolean => isMonthlySalaryEnabled && selectedBillableLinesCount === 0;
